import {PanierValiderOptionsDesc} from 'api-types/ffconnect-hal';
import {useDebouncedCallback} from '../useDebouncedCallback';
import {UseHalStateHook} from '@synako/haligator';
import {CommandeProblem} from 'api-types/ffconnect';

const hasReferenceProblem = (
  problems: CommandeProblem[] | undefined,
): boolean => {
  return problems?.some((problem) => problem.code === 'reference') ?? false;
};

interface ChangerReferenceCommandHook {
  submit: (payload: {reference: string}) => void;
  isDisabled: boolean;
  isSubmitting: boolean;
}

interface UseReferenceCommandeArgs {
  validerOptions: UseHalStateHook<PanierValiderOptionsDesc>;
  changerReferenceCommand: ChangerReferenceCommandHook;
  debounceMs?: number;
}

interface UseReferenceCommandeResult {
  isReferenceError: boolean;
  ifReferenceRequired: boolean;
  handleReferenceChange: (reference: string) => void;
}

/**
 * Hook pour gérer la logique de la référence de commande client.
 *
 * @param validerOptions L'état retourné par useHalState pour les options de validation (`validerOptions`).
 * @param changerReferenceCommand La commande pour changer la référence (ex: `panierWithCommands.changerReference`).
 * @param debounceMs Délai (en ms) pour le debounce de la mise à jour (défaut: 300ms).
 */
export const useReferenceCommande = ({
  validerOptions,
  changerReferenceCommand,
  debounceMs = 300,
}: UseReferenceCommandeArgs): UseReferenceCommandeResult => {
  const validerOptionsData = validerOptions.data;

  const isReferenceError = hasReferenceProblem(
    validerOptionsData?.commande_problems,
  );
  const ifReferenceRequired =
    !!validerOptionsData?.reference_de_commande_client_obligatoire;

  const handleReferenceChange = useDebouncedCallback((reference: string) => {
    if (
      changerReferenceCommand.isDisabled ||
      changerReferenceCommand.isSubmitting
    ) {
      return;
    }
    changerReferenceCommand.submit({reference});
  }, debounceMs);

  return {
    isReferenceError,
    ifReferenceRequired,
    handleReferenceChange,
  };
};
